'use es6';

import PropTypes from 'prop-types';
import { memo } from 'react';
import { useDrop } from 'react-dnd';
import { isEmpty } from 'underscore';
import DashedDropZone from './DashedDropZone';
import { isFieldContactProperty } from 'FormsUI/utils/DragAndDrop';
import { DragTypes } from 'FormsUI/constants/DragAndDrop';
import { trackEditorInteraction } from 'FormsUI/utils/TrackingUtils';
import { DRAG_DROP_REORDERED_FORM_FIELD_TO_EXISTING_GROUP, DRAG_DROP_ADDED_FORM_FIELD_FROM_PANEL_TO_EXISTING_GROUP } from 'FormsUI/constants/TrackingInteractionKeys/Editor/FormTab';
import { jsx as _jsx } from "react/jsx-runtime";
const MAX_FORM_FIELDS_PER_FORM_FIELD_GROUP = 3;
const FormFieldDropTarget = /*#__PURE__*/memo(({
  formFieldGroupIndex,
  formFieldIndex,
  numberFormFieldsWithinGroup,
  isQueued,
  addFormField,
  moveFormFieldIntoExistingFormFieldGroup
}) => {
  const [{
    hasDragOver,
    isDragSourceDroppable
  }, drop] = useDrop({
    accept: [DragTypes.NEW_FORM_FIELD, DragTypes.REORDER_FORM_FIELD],
    canDrop: (item, monitor) => {
      const draggedItemType = monitor.getItemType();
      const draggedItem = item;
      if (isQueued) {
        if (!isFieldContactProperty(draggedItem)) {
          return false;
        }
      }
      if (draggedItem.isPageBreak) {
        return false;
      }
      if (!isEmpty(draggedItem.richTextContent)) {
        return false;
      }
      const hasLessThanThreeFormFields = numberFormFieldsWithinGroup < MAX_FORM_FIELDS_PER_FORM_FIELD_GROUP;
      if (draggedItemType === DragTypes.NEW_FORM_FIELD) {
        return hasLessThanThreeFormFields;
      }
      const sourceFormFieldGroupIndex = draggedItem.formFieldGroupIndex;
      const sourceFormFieldIndex = draggedItem.formFieldIndex;
      const targetFormFieldGroupIndex = formFieldGroupIndex;
      const targetFormFieldIndex = formFieldIndex;
      const areSameGroup = sourceFormFieldGroupIndex === targetFormFieldGroupIndex;
      const isDroppedLeft = sourceFormFieldIndex === targetFormFieldIndex;
      const isDroppedRight = sourceFormFieldIndex + 1 === targetFormFieldIndex;
      if (areSameGroup) {
        return !isDroppedLeft && !isDroppedRight;
      }
      return hasLessThanThreeFormFields; // different form field group, target group having less than 3 form fields
    },
    drop: (item, monitor) => {
      if (monitor.didDrop()) {
        return;
      }
      const draggedItemType = monitor.getItemType();
      const draggedItem = item;
      const targetFormFieldGroupIndex = formFieldGroupIndex;
      const targetFormFieldIndex = formFieldIndex;
      if (draggedItemType === DragTypes.NEW_FORM_FIELD) {
        const propertyId = draggedItem.propertyId;
        addFormField(propertyId, targetFormFieldGroupIndex, targetFormFieldIndex, {
          newFieldType: draggedItem.fieldType
        });
        trackEditorInteraction(DRAG_DROP_ADDED_FORM_FIELD_FROM_PANEL_TO_EXISTING_GROUP);
      } else if (draggedItemType === DragTypes.REORDER_FORM_FIELD) {
        const sourceFormFieldGroupIndex = draggedItem.formFieldGroupIndex;
        const sourceFormFieldName = draggedItem.formFieldName;
        const sourceFormFieldPropertyObjectType = draggedItem.formFieldPropertyObjectType;
        moveFormFieldIntoExistingFormFieldGroup(sourceFormFieldGroupIndex, sourceFormFieldName, sourceFormFieldPropertyObjectType, targetFormFieldGroupIndex, targetFormFieldIndex);
        trackEditorInteraction(DRAG_DROP_REORDERED_FORM_FIELD_TO_EXISTING_GROUP);
      }
    },
    collect: monitor => ({
      hasDragOver: monitor.isOver(),
      isDragSourceDroppable: monitor.canDrop()
    })
  });
  if (!isDragSourceDroppable) {
    return null;
  }
  return /*#__PURE__*/_jsx("div", {
    ref: drop,
    children: /*#__PURE__*/_jsx(DashedDropZone, {
      isVertical: true,
      isHover: hasDragOver
    })
  });
});
FormFieldDropTarget.propTypes = {
  formFieldGroupIndex: PropTypes.number.isRequired,
  formFieldIndex: PropTypes.number.isRequired,
  numberFormFieldsWithinGroup: PropTypes.number.isRequired,
  isQueued: PropTypes.bool,
  addFormField: PropTypes.func.isRequired,
  moveFormFieldIntoExistingFormFieldGroup: PropTypes.func.isRequired
};
export default FormFieldDropTarget;